import React, { useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import FontIcon from 'components/icons/FontIcon';
import portalLogoIcon from 'assets/images/portal-logo.svg';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { EpdLinks } from 'routes/EpdRoutes';
import { searchEpds } from 'services/api/adminApi';
import { getEPD } from 'services/api/epdApi';
import { toaster } from 'components/v2/toast';
import { PoorMansError } from 'util/utils';
import { closeHeaderEmitter, media } from './headerHelpers';
import CustomHeaderMenu, { cutstomHeaderMenuChangedEmitter, setCustomHeaderMenuEmitter } from './CustomHeaderMenu';
import { TextButton } from 'components/v2/buttons';

export type HeaderMenuItem = {
  label: string;
  url?: string;
  target?: '_blank';
  visible?: boolean;
  items?: HeaderMenuItem[];
  isCurrentPage?: boolean;
};

type ExpandedItem = { itemIndex: number; expandedChildren: ExpandedItem[] };

interface Props {
  model: HeaderMenuItem[];
  logoLinkPath: string;
  menuEnd: React.ReactNode;
  drawerStart: React.ReactNode;
  isImpersonated: boolean;
  isSystemAdmin: boolean;
}

const MainHeader: React.FC<Props> = ({ model, logoLinkPath, menuEnd, drawerStart, isImpersonated, isSystemAdmin }) => {
  const [isOpenForMobile, setOpenForMobile] = useState<boolean>(false);
  const [expandedItems, setExpandedItems] = useState<ExpandedItem[]>([]);
  const [registrationNumberSearch, setRegistrationNumberSearch] = useState<string>('');
  const [isSearchingByRegistrationNumber, setIsSearchingByRegistrationNumber] = useState<boolean>(false);
  const navRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const isDrawer = useMediaQuery(media.LESS_THAN_DESKTOP);
  const history = useHistory();
  const { changeCompany } = useContext(CompanyContext);

  const closeHeader = () => {
    setOpenForMobile(false);
    setExpandedItems([]);
  };

  useEffect(() => {
    const handleClickOutsideOverlay = (event: MouseEvent) => {
      if (!menuRef.current || !navRef.current) {
        return;
      }

      const clickTarget = event.target as Node;
      if (isDrawer) {
        if (!navRef.current.contains(clickTarget)) {
          closeHeader();
          setCustomHeaderMenuEmitter.emitEvent(null);
        }

        return;
      }

      if ((menuRef.current !== clickTarget || isDrawer) && menuRef.current.contains(clickTarget)) {
        return;
      }
      setExpandedItems([]);
    };
    document.addEventListener('click', handleClickOutsideOverlay);

    const removeCloseListener = closeHeaderEmitter.addListener(closeHeader);

    return () => {
      document.removeEventListener('click', handleClickOutsideOverlay);
      removeCloseListener();
    };
  }, [isDrawer]);

  const handleKeyboard = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeHeader();
    }
  };

  const navigateToEpdByRegistrationNumber = async () => {
    const registrationNumber = registrationNumberSearch.trim();
    if (!registrationNumber || isSearchingByRegistrationNumber) {
      return;
    }

    try {
      setIsSearchingByRegistrationNumber(true);

      const result = await searchEpds({
        registrationNumber,
        title: null,
        companyId: null,
        licenseeId: null,
        agentId: null,
        epdFormat: null,
        status: null,
        excludeCopyGroupEpds: false,
        excludeEPDsWithoutRegistrationDate: false,
        page: 1,
        pageSize: 1,
        sortBy: null,
        sortDirection: null,
      });

      const epdId = result.items[0]?.epdId;
      if (!epdId) {
        toaster({
          severity: 'warning',
          summary: 'No EPD found',
          details: `Could not find an EPD with registration number ${registrationNumber}.`,
        });
        return;
      }

      const epd = await getEPD(epdId);
      await changeCompany?.(epd?.epdOwner?.companyId);
      history.push(EpdLinks.epd(epdId));
      setRegistrationNumberSearch('');
    } catch (error) {
      toaster({
        severity: 'error',
        summary: 'Error occurred',
        details: PoorMansError(error),
      });
    } finally {
      setIsSearchingByRegistrationNumber(false);
    }
  };

  return (
    <StyledNav ref={navRef} $isImpersonated={isImpersonated} onKeyDown={handleKeyboard}>
      <MobileNavToggleWrapper $isOpenForTablet={isOpenForMobile}>
        <MobileNavToggle
          onClick={() => {
            setOpenForMobile(!isOpenForMobile);
            setCustomHeaderMenuEmitter.emitEvent(null);
          }}
          aria-label="Toggle navigation"
        >
          {isOpenForMobile ? headerCrossIcon : headerBarsIcon}
        </MobileNavToggle>
      </MobileNavToggleWrapper>

      <LogoWrapper>
        <LogoLink to={logoLinkPath} onClick={closeHeader} aria-label="To the home page">
          <LogoImage src={portalLogoIcon} alt="The EPD Portal" />
        </LogoLink>
      </LogoWrapper>

      <MenuItems ref={menuRef} $isOpenForTablet={isOpenForMobile}>
        {drawerStart}

        {model.map((m, itemIndex) => {
          if (m.visible === false) {
            return null;
          }

          if (!m.items || m.items.length === 0) {
            return <SimpleLinkItem key={itemIndex} item={m} nestingLevelIndex={0} />;
          }

          return (
            <MultiLevelItem
              key={itemIndex}
              item={m}
              itemIndex={itemIndex}
              nestingLevelIndex={0}
              expandedItems={expandedItems}
              onExpandedItemsChange={setExpandedItems}
              isDrawer={isDrawer}
            />
          );
        })}
      </MenuItems>

      {isSystemAdmin && (
        <SystemAdminSearchWrapper>
          <SystemAdminSearchInputBox>
            <SystemAdminSearchInput
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={registrationNumberSearch}
              onChange={(e) => setRegistrationNumberSearch(e.currentTarget.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  navigateToEpdByRegistrationNumber();
                }
              }}
              placeholder="Reg.no.search&go"
              aria-label="Search EPD by registration number"
              maxLength={10}
              disabled={isSearchingByRegistrationNumber}
            />
            <SystemAdminSearchButton
              type="button"
              text
              onClick={navigateToEpdByRegistrationNumber}
              disabled={!registrationNumberSearch || isSearchingByRegistrationNumber}
              aria-label="Search"
            >
              ⚙
            </SystemAdminSearchButton>
          </SystemAdminSearchInputBox>
        </SystemAdminSearchWrapper>
      )}

      <EndWrapper>{menuEnd}</EndWrapper>

      <CustomHeaderMenu />
    </StyledNav>
  );
};

export default MainHeader;

function useMediaQuery(mediaQuery: string) {
  const queryRef = useRef<MediaQueryList | null>(null);
  if (!queryRef.current && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    queryRef.current = window.matchMedia(mediaQuery);
  }
  const [queryResult, setQueryResult] = useState<boolean>(queryRef.current?.matches ?? false);

  useLayoutEffect(() => {
    const query = queryRef.current;
    if (!query) {
      return;
    }

    const handleMediaQueryChange = (e: MediaQueryListEvent) => {
      setQueryResult(e.matches);
    };

    query.addEventListener('change', handleMediaQueryChange);
    return () => query.removeEventListener('change', handleMediaQueryChange);
  }, []);

  return queryResult;
}

const StyledNav = styled.nav<{ $isImpersonated: boolean }>`
  --header-height: 80px;
  --header-background: #ffffff;
  --epdi-logo-aspect-ratio: calc(69 / 30);
  --epdi-logo-width: 80px;
  --epdi-logo-height: calc(var(--epdi-logo-width) / var(--epdi-logo-aspect-ratio));
  --padding-size: calc((var(--header-height) - var(--epdi-logo-height)) / 2);
  --mobile-menu-item-inline-padding: 10px;
  --drawer-width: 402px;

  position: fixed;
  top: var(--billing-banner-height, 0px);
  width: 100%;
  height: var(--header-height);
  padding-left: 40px;
  padding-right: 40px;
  z-index: 10;
  display: flex;
  background-color: var(--header-background);
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.05);

  font-family: Inter;
  font-size: 14px;

  ${(props) =>
    props.$isImpersonated &&
    css`
      --header-background: #e8f1e9;
    `}

  @media ${media.LESS_THAN_DESKTOP} {
    --header-height: 60px;
    --epdi-logo-width: 83px;

    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding-left: var(--padding-size);
    padding-right: var(--padding-size);
  }
`;

const drawerAnimation = css`
  transform: translateX(0);
  transition: transform 100ms ease-out;
  @starting-style {
    transform: translateX(calc(-1 * var(--drawer-width)));
  }
`;

const MobileNavToggleWrapper = styled.div<{ $isOpenForTablet: boolean }>`
  display: none;

  @media ${media.LESS_THAN_DESKTOP} {
    display: block;
  }

  @media ${media.LESS_THAN_DESKTOP} {
    ${(props) =>
      props.$isOpenForTablet &&
      css`
        &::after {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: var(--drawer-width);
          background-color: var(--header-background);
          box-shadow: 4px 0 10px 0 rgba(0, 0, 0, 0.05);
          z-index: 1;

          ${drawerAnimation}
        }
      `}
  }

  @media ${media.LESS_THAN_TABLET} {
    ${(props) =>
      // Prevent animation when toggling between mobile & tablet
      props.$isOpenForTablet &&
      css`
        &::after {
          opacity: 0;
          pointer-events: none;
        }
      `}
  }
`;

const MobileNavToggle = styled.button`
  z-index: 2;
  position: fixed;
  top: var(--billing-banner-height, 0px);
  left: 0;
  width: var(--header-height);
  height: var(--header-height);
  padding: 0;
  border: none;
  cursor: pointer;
  background-color: transparent;
`;

const headerBarsIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M4 24H28" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 16H28" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 8H28" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const headerCrossIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path d="M24 8L8 24" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 8L24 24" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LogoWrapper = styled.div`
  @media ${media.LESS_THAN_DESKTOP} {
    display: flex;
    justify-content: center;
  }
`;

const LogoLink = styled(Link)`
  display: block;
  margin-top: var(--padding-size);
  margin-bottom: var(--padding-size);
`;

const LogoImage = styled.img`
  display: block;
  width: var(--epdi-logo-width);
`;

const MenuItems = styled.ul<{ $isOpenForTablet: boolean }>`
  list-style-type: none;
  margin: 0;
  padding: 0;

  @media ${media.DESKTOP_OR_MORE} {
    display: flex;
    align-items: center;
    flex-grow: 1;
    gap: 16px;
    padding-left: 50px;
    padding-right: var(--padding-size);
    padding-top: var(--padding-size);
    padding-bottom: var(--padding-size);
  }

  @media ${media.LESS_THAN_DESKTOP} {
    display: none;
    position: fixed;
    top: var(--billing-banner-height, 0px);
    bottom: 0;
    left: 0;
    width: var(--drawer-width);
    margin-top: var(--header-height);
    background-color: #ffffff;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    box-shadow: 4px 0 10px 0 rgba(0, 0, 0, 0.05);
    font-size: 16px;

    ${drawerAnimation}

    ${(props) =>
      props.$isOpenForTablet &&
      css`
        display: block;
      `}
  }

  @media ${media.LESS_THAN_TABLET} {
    width: auto;
    top: calc(var(--billing-banner-height, 0px) + var(--header-height));
    margin-top: 0;
    right: 0;
    box-shadow: none;
  }
`;

const MenuItem = styled.li`
  position: relative;
`;

const SimpleLinkItem: React.FC<{ item: HeaderMenuItem; nestingLevelIndex: number }> = ({ item, nestingLevelIndex }) => {
  if (!item.url) {
    throw new Error('Item is missing url');
  }
  const isInternalLink = item.url[0] === '/';
  return (
    <MenuItem>
      <MenuItemButton
        as={isInternalLink ? Link : 'a'}
        to={isInternalLink ? item.url : undefined}
        href={isInternalLink ? undefined : item.url}
        onClick={() => closeHeaderEmitter.emitEvent()}
        target={item.target}
        $isCurrentPage={!!item.isCurrentPage}
        $nestingLevelIndex={nestingLevelIndex}
      >
        <span>{item.label}</span>
      </MenuItemButton>
    </MenuItem>
  );
};

const MenuItemButton = styled.button<{
  $isCurrentPage: boolean;
  $nestingLevelIndex: number;
  'data-is-expanded'?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: ${(props) => props.theme.colors.primaryBlack};
  border: none;
  font-size: inherit;
  font-family: inherit;
  cursor: pointer;
  background: none;

  ${(props) =>
    props.$isCurrentPage &&
    css`
      & > span {
        text-decoration: underline;
        color: ${props.theme.colors.primaryGreen};
        font-weight: 600;
      }
    `}

  @media ${media.DESKTOP_OR_MORE} {
    ${(props) => {
      if (props.$nestingLevelIndex == 0) {
        return css`
          padding: 7px 8px;
          min-height: 35px;

          &:hover > span,
          &[data-is-expanded='true'] > span {
            text-decoration: underline;
            color: ${props.theme.colors.primaryGreen};
          }

          &:has(~ ${MultiLevelSubmenu} > ${MenuItem} > &:is([data-is-expanded='true'], :hover)) {
            font-weight: 600;
          }
        `;
      }
      return css`
        padding-top: 9px;
        padding-bottom: 8px;
        padding-left: 16px;
        padding-right: 8px;
        min-width: 174px;
        min-height: 34px;
        display: flex;
        justify-content: space-between;

        &:hover,
        &[data-is-expanded='true'] {
          background-color: ${props.theme.colors.elementsFilledInput};
        }

        ${MenuItem}:first-child > & {
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
        }
        ${MenuItem}:last-child > & {
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
        }

        &:has(~ ${MultiLevelSubmenu} > ${MenuItem} > &:is([data-is-expanded='true'], :hover)) {
          font-weight: 600;
        }
      `;
    }}
  }

  @media ${media.LESS_THAN_DESKTOP} {
    width: 100%;
    min-height: 60px;
    justify-content: space-between;
    padding-top: 19px;
    padding-bottom: 13px;
    padding-left: ${(props) => 20 + props.$nestingLevelIndex * 16}px;
    padding-right: 20px;

    -webkit-tap-highlight-color: transparent;

    & > span {
      transform: translateY(-2px);
    }

    &:active {
      font-weight: 600;
      background-color: ${(props) => props.theme.colors.elementsFilledInput};
    }

    ${(props) =>
      // Root items should be underlined when expanded
      props.$nestingLevelIndex === 0
        ? css`
            &[data-is-expanded='true'] > span {
              text-decoration: underline;
              color: ${props.theme.colors.primaryGreen};
              font-weight: 600;
            }
          `
        : // Nested items should be highlighted with background color when expanded
          css`
            &[data-is-expanded='true'] {
              font-weight: 600;
              background-color: ${props.theme.colors.elementsFilledInput};
            }
          `}

    @media (hover: hover) {
      &:hover {
        background-color: ${(props) => props.theme.colors.elementsFilledInput};
      }
    }
  }
`;

const ChevronIcon = styled(FontIcon)`
  @media ${media.DESKTOP_OR_MORE} {
    transform: translateY(2px);
  }
  @media ${media.LESS_THAN_DESKTOP} {
    transform: translateY(-2px);
  }
`;

const MultiLevelSubmenu = styled.ul<{ $isHidden: boolean; $nestingLevelIndex: number }>`
  list-style-type: none;
  padding: 0;

  @media ${media.DESKTOP_OR_MORE} {
    position: absolute;
    background-color: white;
    z-index: 1;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 8px 20px 0 rgba(0, 0, 0, 0.15);

    ${(props) => {
      if (props.$nestingLevelIndex == 0) {
        return css`
          left: 8px;
        `;
      }
      return css`
        top: 0;
        left: calc(100% + 8px);
      `;
    }}
  }

  ${(props) =>
    props.$isHidden &&
    css`
      display: none !important;
    `}
`;

const MultiLevelItem: React.FC<{
  item: HeaderMenuItem;
  itemIndex: number;
  nestingLevelIndex: number;
  expandedItems: ExpandedItem[];
  onExpandedItemsChange(newItems: ExpandedItem[]): void;
  isDrawer: boolean;
}> = ({ item, itemIndex, nestingLevelIndex, expandedItems, onExpandedItemsChange, isDrawer }) => {
  const toggleButtonId = useId();
  const myExpandedItemEntry =
    isDrawer || expandedItems.length === 1 ? expandedItems.find((itm) => itm.itemIndex === itemIndex) : null;
  const isExpanded = !!myExpandedItemEntry;

  return (
    <MenuItem>
      <MenuItemButton
        id={toggleButtonId}
        aria-haspopup="true"
        aria-expanded={isExpanded ? 'true' : 'false'}
        onClick={(e) => {
          e.preventDefault();

          if (isDrawer) {
            onExpandedItemsChange(
              isExpanded
                ? expandedItems.filter((itm) => itm.itemIndex !== itemIndex)
                : [...expandedItems, { itemIndex, expandedChildren: [] }]
            );
            return;
          }
          onExpandedItemsChange(isExpanded ? [] : [{ itemIndex, expandedChildren: [] }]);
        }}
        onMouseEnter={
          isDrawer || (nestingLevelIndex === 0 && expandedItems.length === 0) || isExpanded
            ? undefined
            : () => onExpandedItemsChange([{ itemIndex, expandedChildren: [] }])
        }
        $isCurrentPage={!!item.isCurrentPage}
        $nestingLevelIndex={nestingLevelIndex}
        data-is-expanded={isExpanded}
      >
        <span>{item.label}</span>
        {isDrawer || nestingLevelIndex === 0 ? (
          <ChevronIcon icon={isExpanded ? 'chevron-up' : 'chevron-down'} />
        ) : (
          <ChevronIcon icon="chevron-right" />
        )}
      </MenuItemButton>
      <MultiLevelSubmenu $isHidden={!isExpanded} $nestingLevelIndex={nestingLevelIndex} aria-labelledby={toggleButtonId}>
        {item.items!.map((sub: HeaderMenuItem, subIndex) => {
          if (sub.visible === false) {
            return null;
          }

          if (!sub.items || sub.items.length === 0) {
            return <SimpleLinkItem key={subIndex} item={sub} nestingLevelIndex={nestingLevelIndex + 1} />;
          }

          return (
            <MultiLevelItem
              key={subIndex}
              item={sub}
              itemIndex={subIndex}
              expandedItems={myExpandedItemEntry?.expandedChildren ?? []}
              onExpandedItemsChange={(newItems) =>
                onExpandedItemsChange(
                  expandedItems.map((itm) => (itm.itemIndex === itemIndex ? { itemIndex, expandedChildren: newItems } : itm))
                )
              }
              nestingLevelIndex={nestingLevelIndex + 1}
              isDrawer={isDrawer}
            />
          );
        })}
      </MultiLevelSubmenu>
    </MenuItem>
  );
};

const EndWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const SystemAdminSearchWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 190px;
  margin-right: 16px;

  @media ${media.LESS_THAN_DESKTOP} {
    display: none;
  }
`;

const SystemAdminSearchInputBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 160px;
  height: 30px;
`;

const SystemAdminSearchInput = styled.input`
  display: block;
  width: 100%;
  height: 30px;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  padding: 0 34px 0 10px;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.primaryGreen};
  }
`;

const SystemAdminSearchButton = styled(TextButton)`
  position: absolute !important;
  top: 1px;
  right: 1px;
  width: 28px;
  height: 28px;
  z-index: 1;
  border: none;
  border-radius: 3px;
  margin: 0;
  padding: 0;
  background-color: transparent;
  color: #6b7280;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${(props) => props.theme.colors.primaryGreen};
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
  }

  &.p-button .p-button-icon {
    margin: 0;
    font-size: 14px;
  }

  &.p-button .p-button-label {
    display: none;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;
